import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest } from '../types/auth';
import { authService } from '../services/authService';
import { useUnsavedGuard } from './UnsavedChangesContext';
import { useConfirm, useConfirmControl } from './ConfirmContext';
import { useFeedback } from './FeedbackContext';
import SessionExpiredDialog, { ReauthReason } from '../components/SessionExpiredDialog';
import {
  onSessionExpired,
  notifySessionExpired,
  resetSessionExpired,
  setSessionNotice,
  clearSessionNotice,
  setLogoutReason,
  getLogoutReason,
  clearLogoutReason,
  getSessionNotice,
  setReauthPending,
  consumeReauthPending,
  clearReauthPending,
  setReauthDialogOpen,
  SessionNotice,
} from '../utils/session';
import { bumpAuthGeneration, onTokenRefreshed, refreshStoredSession } from '../utils/tokenRefresh';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<{ message: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  /**
   * [Web UX 2. csomag, 2026-10-03] Hányszor ért véget a munkamenet (lejárat, másik fül, KÉZI
   * kijelentkezés is). Az `AuthWrapper` ebből tudja, hogy a `/`-ról a belépő oldalra (`/belepes`)
   * kell vinnie; a `Login` ebből tudja, hogy újra kell olvasnia az üzenet-jelzőt. Hogy a belépő
   * oldalon VAN-e „lejárt" üzenet, azt NEM ez dönti el, hanem a `sessionStorage`-jelző (kézi
   * kilépésnél törölve). Opcionális, hogy a tesztek `useAuth` mockjainak ne kelljen ismerniük.
   *
   * [validátor B1, 2026-10-03] Korábban csak a NEM kézi vég számolt, így kézi kijelentkezés után
   * a nyilvános nyitóoldal jött — a régi viselkedés (és két meglévő E2E: `smoke.spec.ts:81-87`,
   * `flow-basics.spec.ts:65-77`) a BELÉPŐ ŰRLAP volt.
   */
  sessionEndCount?: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface ReauthState {
  email: string;
  userId: string;
  labels: string[];
  reason: ReauthReason;
}

function clearStoredAuth() {
  // [Web 7 / T1, 2026-10-05] Egy épp futó csendes megújítás válasza a tároló ürítése után NE írja
  // vissza a tokent (`utils/tokenRefresh.ts` KIJELENTKEZÉS-VERSENY pont).
  bumpAuthGeneration();
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

/**
 * [Web 7 / T1 javító kör 3, 2026-10-06] B1 — a tároló ürítése CSAK ha még az `expected` token van benne
 * (minta: `services/authService.ts` `clearStoredAuthIfUnchanged`). A generáció MINDIG lép (egy itt futó
 * megújítás válasza ne írjon). Miért: a betöltéskori ürítés KORÁBBAN feltétel nélküli volt, és ha közben
 * egy MÁSIK fül írt be egy friss, érvényes tokent, azt törölte → a többi fül `storage` eseményt kapott, és
 * mind kilépett (mérve: 3 fül egyszerre lejárt tokennel → mindhárom `/belepes`).
 */
function clearStoredAuthIfUnchanged(expected: string | null) {
  bumpAuthGeneration();
  if (localStorage.getItem('token') !== expected) return;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

/**
 * A jogosultságot befolyásoló mezők (`types/auth.ts` `User`): a szerepkör (név és azonosító), az
 * aktív és a jóváhagyott állapot. [validátor F2, 2026-10-03] Ha az újra-belépés ezek bármelyikét
 * másként adja vissza, a felület nem maradhat a régi állapottal. Hiányzó előző `user` → változásnak
 * számít (óvatos irány).
 *
 * [2. javító kör, 2026-10-03] Egy MEZŐ csak akkor számít eltérésnek, ha MINDKÉT oldalon ismert
 * (nem `undefined`/`null`) és különbözik: egy régebbi kliens által tárolt `user`-ből hiányozhat egy
 * mező, és az nem jogosultság-változás — különben egy ártalmatlan újra-belépés vagy egy másik fülön
 * történt belépés is kijelentkeztetne (és a mentetlen adat elveszne).
 */
const PERMISSION_FIELDS: Array<keyof User> = ['userRole', 'userRoleId', 'isActive', 'isApproved'];

export function hasPermissionChange(previous: User | null, next: User): boolean {
  if (!previous) return true;
  return PERMISSION_FIELDS.some((field) => {
    const a = previous[field];
    const b = next[field];
    if (a === undefined || a === null || b === undefined || b === null) return false;
    return a !== b;
  });
}

function readStoredUser(): User | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [reauth, setReauth] = useState<ReauthState | null>(null);
  const [sessionEndCount, setSessionEndCount] = useState(0);

  // A jelző és a `storage` esemény kezelője a feliratkozás pillanatától él, ezért a friss
  // állapotot ref-ből olvassa (különben az első render `user === null`-ját látná).
  const userRef = useRef<User | null>(null);
  userRef.current = user;
  const reauthRef = useRef<ReauthState | null>(null);
  reauthRef.current = reauth;
  // A `storage`-útról érkező lejáratnál az ok (pl. kézi kilépés a másik fülön) — a jelző maga ok
  // nélküli, ezért itt adjuk át.
  const pendingReasonRef = useRef<ReauthReason | null>(null);
  // [validátor M2, 2026-10-03] Kézi kijelentkezés folyamatban / megtörtént. A `logout()` a tárolt
  // tokent KORÁBBAN csak a `POST /auth/logout` UTÁN törölte, így az addig befutó, MÉG az élő
  // tokennel küldött kérés 401-e jelzést adott, és a feliratkozó a `'manual'` okot `'expired'`-re
  // írta. [2. javító kör, F-A] Ma a `logout()` a tárolót a hívás ELEJÉN, szinkron üríti, így ilyen
  // jelzés már a `reportUnauthorized` tároló-összevetésén elakad; ez a jelző tartalék-védelemként
  // marad (bármilyen más úton érkező jelzésre). A következő sikeres belépés törli.
  const manualLogoutRef = useRef(false);

  const unsavedLabels = useUnsavedGuard();
  const confirm = useConfirm();
  const { cancelPending: cancelPendingConfirm } = useConfirmControl();
  const { showSuccess, showInfo } = useFeedback();

  /**
   * [7. javító kör — validátor F1, 2026-10-04] Az ablak „Kijelentkezés" kérdése épp nyitva van-e
   * (a `handleReauthExit` `await confirm`-je alatt). Ha közben az ablak MÁS úton bezárul (sikeres
   * újra-belépés, másik fülön újra-belépés), az elavult kérdést lezárjuk „Mégse"-ként.
   */
  const exitConfirmPendingRef = useRef(false);

  /**
   * [7. javító kör — validátor F1/F2] Az újra-belépő állapot EGYETLEN beállító pontja: a ref és a
   * `utils/session.ts` ablak-jelzője SZINKRON frissül (a mentési hibaszöveg a komponens `catch`-ében
   * — még az újrarajzolás ELŐTT — ebből dönti el, hogy a „megmaradtak" mondat igaz-e).
   */
  const applyReauth = useCallback(
    (next: ReauthState | null) => {
      reauthRef.current = next;
      setReauthDialogOpen(next !== null);
      if (next === null && exitConfirmPendingRef.current) {
        exitConfirmPendingRef.current = false;
        cancelPendingConfirm();
      }
      setReauth(next);
    },
    [cancelPendingConfirm]
  );

  /**
   * A munkamenet NEM kézi vége: tárolt adat törlése, üzenet a belépő oldalnak, `user` → null.
   *
   * [2. javító kör, 2026-10-03] `keepStorage`: a `storage`-eseményből (másik fül) induló végnél a
   * tárolóban a MÁSIK fül friss, érvényes belépése van (más felhasználó / új szerepkör) — azt NEM
   * törölhetjük. KORÁBBAN a „más felhasználó a másik fülön" ág itt `clearStoredAuth()`-ot hívott, és
   * ezzel a másik fül épp beírt tokenjét törölte; a másik fül erre (token-törlés `storage`-esemény)
   * a saját, épp megkezdett munkamenetét is lezárta.
   *
   * [7. javító kör — validátor F1] Egy függőben lévő kérdés (pl. „Biztosan törlöd?") a lebomló
   * felület után a belépő oldalon maradt volna, és igen-re a művelet token nélkül futott volna —
   * „Mégse"-ként lezárjuk.
   */
  const endSession = useCallback(
    (notice: SessionNotice, keepStorage = false) => {
      // [Web 7 / T1] a munkamenet vége a megújítás generációját is lépteti (a `keepStorage` ágon is:
      // a tárolóban ott a másik fül belépése, ahhoz egy itt futó megújítás válasza ne nyúljon).
      if (keepStorage) bumpAuthGeneration();
      else clearStoredAuth();
      clearReauthPending();
      setSessionNotice(notice);
      cancelPendingConfirm();
      applyReauth(null);
      setUser(null);
      setSessionEndCount((n) => n + 1);
    },
    [applyReauth, cancelPendingConfirm]
  );

  useEffect(() => {
    const initializeAuth = async () => {
      // [Web 7 / T1 javító kör 3] B1: az induláskor olvasott token — az init csak EZT törölheti.
      let startToken: string | null = null;
      try {
        // [Web UX 2. csomag, 2026-10-03] Újratöltés lejárt tárolt tokennel: korábban CSENDBEN
        // kijelentkeztetett, és a nyilvános nyitóoldal jött (jelentés 4.5). Most, ha VOLT tárolt
        // token, de lejárt, a belépő oldal a „Lejárt a bejelentkezésed…" üzenettel fogad; aki
        // sosem lépett be (vagy kézzel kilépett — akkor a token törölve van), a nyitóoldalt látja.
        startToken = localStorage.getItem('token');
        const hadToken = !!startToken;
        // [2. javító kör — validátor F-B] Újratöltés NYITOTT újra-belépő ablak alatt: a halott
        // tokent a mentetlen ág már törölte, így `hadToken === false` — korábban üzenet nélkül a
        // nyitóoldal jött. A jelző (és az üzenet) a mentetlen ágon áll be (lásd lent).
        const reauthWasPending = consumeReauthPending();
        const currentUser = authService.getCurrentUser();
        if (currentUser && authService.isAuthenticated()) {
          // (pl. közben egy másik fülön ugyanaz a felhasználó újra belépett) — nincs teendő, a
          // függőben maradt üzenetet töröljük, hogy egy későbbi kézi kilépésnél se látszódjon.
          if (reauthWasPending) clearSessionNotice();
          setUser(currentUser);
        } else {
          // [Web 7 / T1, 2026-10-05] Újratöltés LEJÁRT (de a szerver 30 napos türelmi idején belüli)
          // tokennel: KORÁBBAN azonnal a belépő oldal jött „Lejárt…" üzenettel — aki tegnap délután
          // lépett be, ma délután kidobta. Most előbb egy csendes megújítás; sikerrel a felhasználó
          // bent marad (a tároló már az új tokent és a szerver friss felhasználói adatát tartja).
          // Bukásnál (30 napnál régebbi / hamis token, inaktív fiók, hálózati hiba) a régi út jön.
          if (currentUser && hadToken) {
            const refreshed = await refreshStoredSession();
            if (refreshed.kind === 'ok') {
              if (reauthWasPending) clearSessionNotice();
              setUser(refreshed.user);
              return;
            }
            // [Web 7 / T1 javító kör 1, 2026-10-06] F2: ÁTMENETI hiba (5xx, hálózat, 408, 429, időtúllépés) — a szerver épp
            // újraindul / nem elérhető. KORÁBBAN ez is kiléptetett és törölte a tárolót, pedig a token a
            // szerver 30 napos türelmi ideje miatt még megújítható. Ma a tároló MARAD, a felhasználó bent
            // marad a tárolt adatával; a lejárt token miatt a következő kérés indulásakor újra
            // megpróbáljuk (`tokenRefresh.ts` `resolveRequestToken`). [Web 7 / T1 javító kör 2, 2026-10-06] F-A:
            // ha az is ÁTMENETI hibával bukik, a kérés 401-e SEM lejárat — a kérés a „A szerver átmenetileg
            // nem érhető el, a bejelentkezésed megmaradt…" szöveggel bukik, a tároló marad (KORÁBBAN itt a
            // lejárat-út jött: kiléptetés + a tároló ürítése, mérve 1,5 mp alatt). Csak VÉGLEGES elutasítás
            // jelez lejáratot. VÉGLEGES bukásnál (401/403/404 — 30 napnál régebbi / hamis token, inaktív
            // fiók) itt is a régi út.
            if (refreshed.kind === 'transient') {
              if (reauthWasPending) clearSessionNotice();
              setUser(currentUser);
              return;
            }
          }
          // [Web 7 / T1 javító kör 3, 2026-10-06] B1: ha a megújítás alatt egy MÁSIK fül új, érvényes
          // munkamenetet írt a tárolóba (pl. MÁS felhasználó lépett be ott — az azonos felhasználóét már a
          // `refreshStoredSession` átvette), azt NEM töröljük, és ez a fül azzal indul — ugyanaz, amit egy
          // újratöltés mutatna. KORÁBBAN itt feltétel nélküli `clearStoredAuth()` állt (lásd lent).
          // A token lejáratát a GÉP ÓRÁJA szerint itt sem vizsgáljuk (a másik fül épp most kapta a szervertől;
          // indok és mérés: `tokenRefresh.ts` `adoptStoredSession`).
          const nowToken = localStorage.getItem('token');
          if (nowToken && nowToken !== startToken) {
            const nowUser = authService.getCurrentUser();
            if (nowUser && nowUser.userId) {
              if (reauthWasPending) clearSessionNotice();
              setUser(nowUser);
              return;
            }
          }
          // A lejárt tokennel küldött `POST /auth/logout` (a régi `authService.logout()` hívás)
          // úgyis 401 lenne, a szerver-oldali kilépés pedig üres művelet (`AuthController.cs:585-593`)
          // — ezért csak a helyi tárolót ürítjük. [Web 7 / T1 javító kör 3] B1: csak ha MÉG az induláskor
          // olvasott token van benne (egy másik fül által beírtat nem törlünk).
          clearStoredAuthIfUnchanged(startToken);
          if (hadToken) {
            setSessionNotice('expired');
            setSessionEndCount((n) => n + 1);
          } else if (reauthWasPending) {
            if (!getSessionNotice()) setSessionNotice('expired');
            setSessionEndCount((n) => n + 1);
          }
        }
      } catch {
        // Bármilyen váratlan init-hiba esetén se ragadjon be az app a loading képernyőn.
        // [Web 7 / T1 javító kör 3] B1: egy másik fül által közben beírt tokent itt sem törlünk.
        clearStoredAuthIfUnchanged(startToken);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * [Web UX 2. csomag, 2026-10-03] A lejárt munkamenet KÖZÖS kezelése (`utils/session.ts`).
   *
   * 🔴 DÖNTÉS (koordinátor, a user nevében, ideiglenes alapértelmezés): a lejárat NEM dobhatja el a
   * beírt, mentetlen adatot (a jelenléti ív mentésekor jövő 401 után a pipák a régi viselkedés
   * szerint a képernyőn maradtak — `EventAttendance.tsx` mentés-ág). Ezért:
   *   - mentetlen változás NÉLKÜL: azonnali kijelentkeztetés + a belépő oldal az üzenettel;
   *   - mentetlen változással: a Dashboard NEM bomlik le, a felület fölött újra-belépő ablak nyílik
   *     (ugyanaz a felhasználó, csak jelszó kell), és sikeres belépés után a felhasználó újra
   *     megnyomja a Mentés-t.
   * `POST /auth/logout`-ot SZÁNDÉKOSAN nem küldünk: a token a szerver szerint már érvénytelen, a
   * hívás 401 lenne, és a szerver-oldali kilépés úgyis üres művelet (`AuthController.cs:585-593`).
   */
  useEffect(() => {
    return onSessionExpired(() => {
      const reason: ReauthReason = pendingReasonRef.current ?? 'expired';
      pendingReasonRef.current = null;
      if (manualLogoutRef.current) {
        // Kézi kilépés közben befutó 401: a kézi ok marad, nincs „lejárt" üzenet (lásd M2 fent).
        clearStoredAuth();
        return;
      }
      const current = userRef.current;
      if (reason === 'expired') {
        // A többi fül a `token` törlésekor ebből tudja, hogy lejárat volt, nem kézi kilépés.
        setLogoutReason('expired');
      }
      if (!current) {
        clearStoredAuth();
        return;
      }
      const labels = unsavedLabels();
      if (labels.length === 0) {
        endSession(reason === 'otherTabLogout' ? 'otherTabLogout' : 'expired');
        return;
      }
      // A halott tokent a mentetlen ágon is töröljük: a további kérések token nélkül mennek (nem
      // nyitnak újabb ablakot), és a többi fül is értesül. A `user` állapot MARAD — ettől nem
      // bomlik le a Dashboard.
      clearStoredAuth();
      // [2. javító kör — validátor F-B] Ha a felhasználó az ablak alatt újratölt (F5), a belépő
      // oldal a megfelelő üzenettel jöjjön (ne a nyitóoldal üzenet nélkül). A sikeres újra-belépés
      // mindkét jelzőt törli.
      setSessionNotice(reason === 'otherTabLogout' ? 'otherTabLogout' : 'expired');
      setReauthPending();
      // [7. javító kör — validátor F1, 2026-10-04] Egy már nyitva álló kérdés (pl. „Mentetlen
      // változás — elvetés?") „Mégse"-ként zárul: az ablak alatt takarásban maradt volna, és a
      // „Mégse" az adatot MEGTARTJA. (Az újra-belépő ablak alatt a felület nem kattintható — a MUI
      // `aria-hidden` + fókusz-csapda —, így onnan új kérdés nem nyílhat; a saját „Kijelentkezés"
      // kérdésünk pedig a `ConfirmProvider` `zIndex`-e miatt az ablak FÖLÖTT jelenik meg.)
      cancelPendingConfirm();
      applyReauth({ email: current.email, userId: current.userId, labels, reason });
    });
  }, [unsavedLabels, endSession, applyReauth, cancelPendingConfirm]);

  /**
   * [Web 7 / T1, 2026-10-05] Csendes megújítás után: ha a szerver MÁS jogosultsággal (szerepkör,
   * aktív/jóváhagyott állapot) adta vissza a felhasználót, a felület nem maradhat a régi szerepkörrel
   * az új tokennel — ugyanaz a szabály, mint az újra-belépésnél (`handleReauthLogin`) és a másik fülön
   * történt belépésnél (`storage`-kezelő): kijelentkeztetés „Megváltozott a jogosultságod" üzenettel.
   * A tároló ürül, így a megújításra váró kérés sem megy ki (`tokenRefresh.ts` → `AuthSessionChangedError`).
   * Ha csak nem-jogosultsági mező tér el (név, telefon…), a `user` ÁLLAPOT szándékosan marad (egy új
   * objektum a `user`-től függő betöltéseket újrafuttatná, és felülírhatná a beírt adatot); a tárolt
   * `user` már az új, a következő újratöltés azt olvassa.
   */
  useEffect(() => {
    return onTokenRefreshed((fresh) => {
      const current = userRef.current;
      if (!current) return;
      // [Web 7 / T1 javító kör 1, 2026-10-06] F1-V11: ez az ág ÖNÁLLÓ védvonal a `tokenRefresh.ts` `runRefresh` userId-őre
      // mellett: az ott csak akkor fog, ha a tárolóban volt felhasználó (`startUserId`); ha a tárolt
      // `user` hiányzik / sérült, a más felhasználójú válasz csak itt akad el. Teszt:
      // `AuthContext.sessionExpired.test.tsx` „F1-V11".
      if (fresh.userId !== current.userId) {
        endSession('otherUser');
        return;
      }
      if (hasPermissionChange(current, fresh)) endSession('permissionChanged');
    });
  }, [endSession]);

  /**
   * [Web UX 2. csomag, 2026-10-03] Több böngészőfül. A `storage` esemény csak a TÖBBI fülön fut.
   *   - a `token` törlődött (kilépés vagy lejárat egy másik fülön): ugyanaz az út, mint a lejáratnál
   *     (mentetlen adatnál újra-belépő ablak, egyébként kijelentkeztetés). Az üzenet a másik fül
   *     által hagyott okot követi: kézi kilépésnél „Kijelentkeztél egy másik lapon", lejáratnál
   *     „Lejárt a bejelentkezésed" — így egyik sem állít valótlant.
   *   - egy MÁSIK felhasználó került a tárolóba: kijelentkeztetés (mentetlen adattal is) — nem
   *     maradhat az egyik felhasználó felülete a másik tokenjével (egy mentés a másik nevében menne).
   *     A tárolót NEM ürítjük: abban a másik fül érvényes belépése van (2. javító kör).
   *   - ugyanannak a felhasználónak új tokenje, de MÁS szerepkörrel/állapottal: kijelentkeztetés
   *     „Megváltozott a jogosultságod" üzenettel (2. javító kör, `hasPermissionChange`).
   *   - ugyanannak a felhasználónak új tokenje (a másik fülön újra belépett): nincs teendő; ha itt
   *     az újra-belépő ablak nyitva volt, bezárul.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== 'token' && e.key !== 'user') return;
      const current = userRef.current;
      if (!current) return;
      const storedToken = localStorage.getItem('token');
      const storedUser = readStoredUser();

      if (!storedToken) {
        if (reauthRef.current) return; // már újra-belépésre várunk
        pendingReasonRef.current = getLogoutReason() === 'manual' ? 'otherTabLogout' : 'expired';
        notifySessionExpired();
        // Ha a zár már állt (nem kellene, de ha mégis), a feliratkozó nem futott: ne maradjon ok.
        pendingReasonRef.current = null;
        return;
      }

      if (storedUser && storedUser.userId !== current.userId) {
        // `keepStorage`: a tárolóban a másik fül friss belépése van, azt nem töröljük (lásd `endSession`).
        endSession('otherUser', true);
        return;
      }

      // [2. javító kör, 2026-10-03] Ugyanaz a felhasználó, de a másik fülön történt belépés MÁS
      // szerepkört / állapotot hozott (pl. az admin közben edzőből játékossá tette): ez a fül nem
      // maradhat a régi szerepkör felületével az új tokennel — ugyanaz a szabály, mint az
      // újra-belépő ablakban (`handleReauthLogin`, validátor F2).
      if (storedUser && hasPermissionChange(current, storedUser)) {
        endSession('permissionChanged', true);
        return;
      }

      if (reauthRef.current && storedUser && storedUser.userId === current.userId) {
        resetSessionExpired();
        clearSessionNotice();
        clearReauthPending();
        applyReauth(null);
        showInfo('Egy másik lapon újra beléptél, folytathatod a munkát.');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [endSession, showInfo, applyReauth]);

  const login = async (credentials: LoginRequest) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      // [Web 7 / T1] egy előző munkamenetből még futó megújítás válasza ne írhassa felül az új belépést.
      bumpAuthGeneration();
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      // Sikeres belépés: a lejárt-üzenet, a kilépés-ok és a lejárt-zár törlődik.
      clearSessionNotice();
      clearReauthPending();
      clearLogoutReason();
      manualLogoutRef.current = false;
      resetSessionExpired();
      setUser(response.user);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      throw error; // Re-throw the error so Login component can handle it
    }
  };

  const register = async (userData: RegisterRequest): Promise<{ message: string }> => {
    setLoading(true);
    try {
      const response = await authService.register(userData);
      // Do not automatically log in the user after registration
      // The user needs admin approval first
      return response;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Kézi kijelentkezés: a belépő oldalon NINCS „lejárt" üzenet, a többi fül pedig a
    // „Kijelentkeztél egy másik lapon" mondatot kapja (az okot a token törlése ELŐTT írjuk ki).
    manualLogoutRef.current = true;
    clearSessionNotice();
    clearReauthPending();
    setLogoutReason('manual');
    applyReauth(null);
    authService.logout();
    setUser(null);
    // [validátor B1] A kézi kilépés után is a BELÉPŐ ŰRLAP jön (`AuthWrapper` → `/belepes`),
    // ahogy a régi felületen; üzenet nélkül, mert a jelzőt fent töröltük.
    setSessionEndCount((n) => n + 1);
  };

  /**
   * Újra-belépés az ablakból. SZÁNDÉKOSAN nem a `login()`-t hívja: az a `loading`-ot billenti,
   * amitől az `AuthWrapper` a teljes oldalas pörgettyűre vált, és a Dashboard (vele a mentetlen
   * adat) lebomlana. A `user` állapotot sem cseréljük (ugyanaz a felhasználó) — egy új objektum a
   * `user`-től függő betöltéseket újrafuttatná, és azok felülírhatnák a beírt állapotot.
   */
  const handleReauthLogin = async (password: string) => {
    const pending = reauthRef.current;
    if (!pending) return;
    const response = await authService.login({ email: pending.email, password });
    if (!response?.user || response.user.userId !== pending.userId) {
      // Biztonsági tartalék: más felhasználó itt nem folytathatja az előző munkáját.
      // [2. javító kör — validátor M-2] Pontos ok a belépő oldalon (korábban „Lejárt…").
      endSession('otherUserReauth');
      return;
    }
    // [validátor F2, 2026-10-03] Ugyanaz a felhasználó, de MEGVÁLTOZOTT JOGOSULTSÁGGAL (pl. az
    // admin közben edzőből játékossá tette): a felület (fülek, gombok, a `user`-ből számolt
    // szerepkör-ágak) a RÉGI szerepkörrel maradna, miközben az új token már az újat hordozza —
    // ezért ugyanaz a teljes kijelentkeztetés, mint eltérő azonosítónál; az új belépés a friss
    // szerepkörrel épít fel mindent. A mentetlen adat ilyenkor elvész, de egy más jogosultságú
    // felületről mentése amúgy is elutasítást kapna.
    if (hasPermissionChange(userRef.current, response.user)) {
      endSession('permissionChanged');
      return;
    }
    // Csak NEM jogosultsági mező (név, telefon, számlázási adat…) térhet el: a TÁROLT `user` az új
    // (a következő újratöltés már azt olvassa), a `user` ÁLLAPOT viszont SZÁNDÉKOSAN marad — egy új
    // objektum a `user`-től függő betöltéseket újrafuttatná, és azok felülírhatnák a beírt,
    // mentetlen adatot (lásd a függvény-fejléc indoklását). Egy elavult megjelenő név ennél kisebb baj.
    bumpAuthGeneration(); // [Web 7 / T1] lásd `login`
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    clearSessionNotice();
    clearReauthPending();
    clearLogoutReason();
    resetSessionExpired();
    applyReauth(null);
    showSuccess('Újra beléptél. Most már elmentheted a változásaidat.');
  };

  const handleReauthExit = async () => {
    const pending = reauthRef.current;
    if (!pending) return;
    const labels = pending.labels;
    // [validátor M4, 2026-10-03] Szóhasználat: „Kijelentkezés", mint a Dashboard gombja.
    exitConfirmPendingRef.current = true;
    let ok: boolean;
    try {
      ok = await confirm({
        title: 'Kijelentkezés',
        message: `Ha most kijelentkezel, a még nem mentett változásaid elvesznek${
          labels.length ? ` (${labels.join(', ')})` : ''
        }.`,
        confirmLabel: 'Kijelentkezés',
        cancelLabel: 'Mégse',
        destructive: true,
      });
    } finally {
      exitConfirmPendingRef.current = false;
    }
    if (!ok) return;
    // [7. javító kör — validátor F1, 2026-10-04] A kérdés alatt az ablak MÁS úton bezárulhatott
    // (sikeres újra-belépés itt vagy egy másik fülön) — akkor a válasz egy már nem létező állapotra
    // vonatkozik. KORÁBBAN az elavult „igen" a FRISS munkamenetet is kidobta (token törlés, akár a
    // másik fül új belépéséé is, és valótlan „Lejárt…" üzenet). Csak UGYANAZ a re-auth állapot
    // jelentkeztethet ki.
    if (reauthRef.current !== pending) return;
    endSession(pending.reason === 'otherTabLogout' ? 'otherTabLogout' : 'expired');
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user && authService.isAuthenticated(),
    sessionEndCount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredDialog
        open={!!reauth}
        email={reauth?.email ?? ''}
        labels={reauth?.labels ?? []}
        reason={reauth?.reason ?? 'expired'}
        onLogin={handleReauthLogin}
        onExit={handleReauthExit}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
